/**
 * apps/web :: pages :: CheckoutPage
 *
 * `/checkout?tier=...` — bridges the paywall picker to Stripe's hosted
 * Checkout page (ROADS-445 FE-02 + TAX-01).
 *
 * Flow:
 *   1. Read `tier` from the query string. Reject anything outside the
 *      EntitlementTier union with a "pick a tier from the paywall" copy
 *      (the user landed here directly without going through the paywall).
 *   2. Read state/country from the user profile:
 *        - class_c / cdl / motorcycle: use `current_focus_state`. Ask
 *          the user to confirm before redirecting.
 *        - tourist_in: no region needed — just confirm + go.
 *        - tourist_out: ask for a destination country (we don't store
 *          one on the profile yet; that's a future ticket).
 *   3. POST to /api/checkout/session. Redirect via
 *      `window.location.href = checkout_url`. We intentionally do NOT
 *      use react-router's navigate because Stripe lives on a different
 *      origin and we want a full hard navigation.
 *   4. On 503 / network error, show a "try again in a minute" surface
 *      with a retry button. On 400 (validation_failed) show the
 *      backend's `message` so the user can correct the input.
 *
 * ROADS-436 status-agnostic: the page never asks citizenship,
 * residency, or visa — only the destination state/country, which is
 * what tier+entitlement actually require.
 *
 * Pencil source-of-truth: no /checkout design currently in the active
 * .pen — this scaffolds with brand tokens (Tailwind tokens + shadcn
 * patterns) and ships flagged for design review.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import UpgradeCallout, {
  creditEstimateUsd,
  daysRemainingFor,
} from "@/components/paywall/UpgradeCallout";
import { COUNTRIES } from "@/lib/countries";
import { listPacks } from "@/lib/pass-content";
import { ApiError } from "@/lib/api";
import {
  getBillingProvider,
  type BillingProvider,
} from "@/lib/billing-provider-api";
import { createCheckoutSession } from "@/lib/checkout-api";
import { createPaddleCheckoutSession } from "@/lib/paddle-checkout-api";
import {
  listMyEntitlements,
  type Entitlement,
} from "@/lib/entitlement-api";
import { getProfile, type UserProfile } from "@/lib/profile-api";
import type { EntitlementTier } from "@/lib/entitlement-api";

type Stage =
  | { kind: "loading" }
  | {
      kind: "ready";
      profile: UserProfile | null;
      /** Source tourist entitlement for FE-04 upgrade flow; null otherwise. */
      upgradeFrom: Entitlement | null;
      /** Server-clock at lookup time (entitlements .now_ms). */
      nowMs: number;
      /**
       * ROADS-451 PR-4: active billing lane. Drives whether `handleSubmit`
       * calls `/api/checkout/session` (Stripe legacy) or
       * `/api/billing/paddle/checkout` (MoR cutover). Resolved on mount
       * via `getBillingProvider()`; the redirecting / error stages also
       * carry it so we can render lane-specific copy.
       */
      billingProvider: BillingProvider;
    }
  | { kind: "redirecting"; billingProvider: BillingProvider }
  | { kind: "error"; message: string };

const KNOWN_TIERS: ReadonlySet<EntitlementTier> = new Set<EntitlementTier>([
  "class_c",
  "cdl",
  "motorcycle",
  "tourist_in",
  "tourist_out",
]);

function parseTier(raw: string | null): EntitlementTier | null {
  if (!raw) return null;
  return KNOWN_TIERS.has(raw as EntitlementTier)
    ? (raw as EntitlementTier)
    : null;
}

/** Whether this tier requires a US state code on the request body. */
function tierRequiresState(tier: EntitlementTier): boolean {
  return tier === "class_c" || tier === "cdl" || tier === "motorcycle";
}

/** Whether this tier requires a destination country code. */
function tierRequiresCountry(tier: EntitlementTier): boolean {
  return tier === "tourist_out";
}

/**
 * Tourist→tourist isn't an upgrade. The backend rejects with 400; we
 * never attach the upgrade id in those cases.
 */
function isUpgradeDestinationTourist(tier: EntitlementTier): boolean {
  return tier === "tourist_in" || tier === "tourist_out";
}

export default function CheckoutPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const tier = parseTier(search.get("tier"));
  const upgradeFromIdParam = search.get("upgrade_from_entitlement_id");
  const countryParam = search.get("country");

  // tourist_out is the only seeded country-scoped tier; its FOREIGN-destination
  // packs (IT/CH/FR) are the only valid outbound checkout countries. The
  // pass-content set also carries a `US` pack (home-country reference, not an
  // outbound "drive abroad" destination), so it must never surface in the
  // tourist_out picker. We constrain BOTH the `?country=` prefill and the
  // submit guard to this set so a hand-edited or stale deep-link can't
  // preselect or submit an unsupported country — the backend charges a flat
  // $15 for tourist_out regardless of country, so a bad code would otherwise
  // mean paying for an empty pass.
  const supportedCountryCodes = useMemo(
    () =>
      new Set(
        listPacks()
          .map((p) => p.jurisdiction.id.toUpperCase())
          .filter((code) => code !== "US"),
      ),
    [],
  );

  const [stage, setStage] = useState<Stage>({ kind: "loading" });

  // Local form state — only used for tier/region the profile doesn't
  // already cover (tourist_out has no profile field for destination
  // country yet).
  const [stateCode, setStateCode] = useState<string>("");
  const [countryCode, setCountryCode] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    if (tier === null) {
      setStage({
        kind: "error",
        message: t("checkout.error.missingTier"),
      });
      return () => {
        cancelled = true;
      };
    }
    void (async () => {
      try {
        // ROADS-451 PR-4: fetch billing provider in parallel with the
        // profile. `/api/billing/provider` is anonymous so it never
        // blocks on auth; on failure we fall back to the Stripe lane
        // since the legacy /api/checkout/* route remains available
        // throughout the cutover window.
        const [profile, billingProviderRaw] = await Promise.all([
          getProfile(),
          getBillingProvider().catch(() => "stripe" as BillingProvider),
        ]);
        const billingProvider: BillingProvider = billingProviderRaw;
        if (cancelled) return;
        if (profile && tierRequiresState(tier) && profile.current_focus_state) {
          setStateCode(profile.current_focus_state);
        }
        // tourist_out: prefill the destination country from the deep-link
        // (?country=IT) so the marketing pack cards land the user on a
        // pre-filled checkout. Upper-cased to match pack ids. Only accept a
        // country that's actually a seeded pack — an unsupported code is
        // dropped so the user must pick (and can't silently submit it).
        if (tierRequiresCountry(tier) && countryParam) {
          const code = countryParam.toUpperCase();
          if (tier !== "tourist_out" || supportedCountryCodes.has(code)) {
            setCountryCode(code);
          }
        }
        // FE-04: when the URL carries an upgrade-source id, resolve it
        // against the entitlement list so we know the days-remaining
        // (server clock + expiry). The backend re-validates ownership +
        // tier + activity at session-create time; this is just for the
        // pre-checkout callout copy. On the Paddle lane the credit
        // math runs server-side (PATCH /subscriptions proration), so
        // skip the lookup entirely — the callout would be misleading.
        let upgradeFrom: Entitlement | null = null;
        let serverNowMs = Date.now();
        if (
          billingProvider === "stripe" &&
          upgradeFromIdParam &&
          !isUpgradeDestinationTourist(tier)
        ) {
          try {
            const ent = await listMyEntitlements();
            serverNowMs = ent.now_ms;
            upgradeFrom =
              ent.entitlements.find(
                (e) =>
                  e.id === upgradeFromIdParam &&
                  (e.tier === "tourist_in" || e.tier === "tourist_out") &&
                  e.expires_at_ms > ent.now_ms,
              ) ?? null;
          } catch {
            // If the entitlement lookup fails the upgrade callout is
            // hidden but the rest of the page still renders. The
            // backend remains the source of truth at session-create
            // time.
          }
        }
        setStage({
          kind: "ready",
          profile,
          upgradeFrom,
          nowMs: serverNowMs,
          billingProvider,
        });
      } catch (err) {
        if (cancelled) return;
        setStage({
          kind: "error",
          message:
            err instanceof Error
              ? err.message
              : t("checkout.error.profileLoadFailed"),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tier, t, upgradeFromIdParam, countryParam, supportedCountryCodes]);

  // tourist_out is the only country-scoped tier and only the seeded packs
  // (IT/CH/FR) have content, so the picker is constrained to them. Other
  // country-scoped tiers (none today) would fall back to the full list.
  const sortedCountries = useMemo(() => {
    if (tier === "tourist_out") {
      return [...COUNTRIES]
        .filter((c) => supportedCountryCodes.has(c.code))
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    return [...COUNTRIES].sort((a, b) => a.name.localeCompare(b.name));
  }, [tier, supportedCountryCodes]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (tier === null) return;
    if (stage.kind !== "ready") return;

    if (tierRequiresState(tier) && !stateCode) {
      setStage({
        kind: "error",
        message: t("checkout.error.stateRequired"),
      });
      return;
    }
    // For tourist_out, require a country that's actually a seeded pack —
    // guard on React state (not the DOM `required`), so an unsupported
    // prefilled value can never be submitted for a real charge.
    const countryInvalid =
      tier === "tourist_out"
        ? !supportedCountryCodes.has(countryCode)
        : !countryCode;
    if (tierRequiresCountry(tier) && countryInvalid) {
      setStage({
        kind: "error",
        message: t("checkout.error.countryRequired"),
      });
      return;
    }

    const billingProvider = stage.billingProvider;
    setStage({ kind: "redirecting", billingProvider });
    try {
      if (billingProvider === "paddle") {
        // ROADS-451 PR-4: Paddle lane. Proration / upgrade credit is
        // applied server-side via PATCH /subscriptions/{id} when the
        // webhook fires — there's no client-side credit echo, so we
        // intentionally skip the Stripe-era sessionStorage write.
        const paddleResp = await createPaddleCheckoutSession({
          tier,
          state_code: tierRequiresState(tier) ? stateCode : null,
          country_code: tierRequiresCountry(tier) ? countryCode : null,
        });
        // Hard navigation to Paddle-hosted checkout
        // (checkout.paddle.com). window.location.href is the right
        // primitive — react-router cannot navigate cross-origin.
        window.location.href = paddleResp.checkout_url;
        return;
      }

      // Stripe lane (legacy). Unchanged from FE-02 + FE-04.
      const resp = await createCheckoutSession({
        tier,
        state_code: tierRequiresState(tier) ? stateCode : null,
        country_code: tierRequiresCountry(tier) ? countryCode : null,
        // FE-04: only attach the upgrade id when the destination is a
        // resident tier; the backend rejects tourist→tourist with 400
        // and we never want the FE to send a request that's guaranteed
        // to fail.
        upgrade_from_entitlement_id:
          upgradeFromIdParam && !isUpgradeDestinationTourist(tier)
            ? upgradeFromIdParam
            : undefined,
      });
      // FE-04: stash the credit-applied amount in sessionStorage so the
      // /checkout/success page can show "Credit of \$X applied". The
      // backend remains the source of truth for the actual credit (the
      // webhook handler is what applies it); this is a UI-only echo.
      // sessionStorage rather than localStorage so it doesn't survive
      // tab close, and a single key so we don't fragment state.
      if (resp.credit_applied_cents && resp.credit_applied_cents > 0) {
        try {
          window.sessionStorage.setItem(
            "rb_checkout_upgrade_credit",
            JSON.stringify({
              session_id: resp.session_id,
              credit_applied_cents: resp.credit_applied_cents,
              credit_method: resp.credit_method ?? "expiry_extension",
            }),
          );
        } catch {
          // sessionStorage can be disabled (Safari ITP, embedded
          // iframes); the success page falls back gracefully when the
          // key is missing.
        }
      }
      // Hard navigation to Stripe-hosted Checkout. window.location.href
      // is the right primitive here — Stripe lives on checkout.stripe.com
      // and react-router cannot navigate cross-origin.
      window.location.href = resp.checkout_url;
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 503
          ? t("checkout.error.unavailable")
          : err instanceof ApiError
            ? err.message
            : t("checkout.error.network");
      setStage({ kind: "error", message });
    }
  }

  function handleBack(): void {
    navigate("/dashboard");
  }

  function handleRetry(): void {
    if (tier !== null) {
      setStage({ kind: "loading" });
      void Promise.all([
        getProfile(),
        // Provider lookup tolerates failure — fall back to Stripe so the
        // user can still complete a purchase via the legacy lane.
        getBillingProvider().catch(() => "stripe" as BillingProvider),
      ])
        .then(([profile, billingProvider]) => {
          if (tierRequiresState(tier) && profile?.current_focus_state) {
            setStateCode(profile.current_focus_state);
          }
          // Retry-path keeps the upgrade callout off; the user has just
          // hit an error so we don't want to introduce a second moving
          // part. They land on the form and submit when ready.
          setStage({
            kind: "ready",
            profile,
            upgradeFrom: null,
            nowMs: Date.now(),
            billingProvider,
          });
        })
        .catch((err: unknown) => {
          setStage({
            kind: "error",
            message:
              err instanceof Error
                ? err.message
                : t("checkout.error.profileLoadFailed"),
          });
        });
    }
  }

  return (
    <LearnerPortalShell pageTitle={t("checkout.title")}>
      <section
        data-testid="checkout-page"
        className="mx-auto max-w-2xl px-md py-lg"
      >
        <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">
          {t("checkout.title")}
        </h1>
        <p className="mt-sm text-sm text-slate-600">
          {tier
            ? t("checkout.subtitleWithTier", { tier: t(`paywall.tiers.${tierI18nKey(tier)}.name`) })
            : t("checkout.subtitle")}
        </p>

        {stage.kind === "loading" && (
          <div
            data-testid="checkout-loading"
            role="status"
            aria-live="polite"
            className="mt-lg flex items-center gap-sm text-slate-600"
          >
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            <span>{t("checkout.loading")}</span>
          </div>
        )}

        {stage.kind === "redirecting" && (
          <div
            data-testid="checkout-redirecting"
            role="status"
            aria-live="polite"
            className="mt-lg flex items-center gap-sm text-slate-700"
          >
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            <span>
              {stage.billingProvider === "paddle"
                ? t("checkout.redirectingToPaddle", {
                    defaultValue: "Redirecting to Paddle checkout…",
                  })
                : t("checkout.redirecting")}
            </span>
          </div>
        )}

        {stage.kind === "error" && (
          <div
            data-testid="checkout-error"
            role="alert"
            className="mt-lg rounded-xl border border-red-200 bg-red-50 px-md py-md text-red-900"
          >
            <p className="text-sm font-semibold">{t("checkout.error.heading")}</p>
            <p className="mt-xs text-sm">{stage.message}</p>
            <div className="mt-md flex gap-sm">
              <button
                type="button"
                onClick={handleRetry}
                className="inline-flex h-11 min-w-[44px] items-center justify-center rounded-xl bg-teal-600 px-md text-sm font-semibold text-white hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
              >
                {t("checkout.error.retry")}
              </button>
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex h-11 min-w-[44px] items-center justify-center rounded-xl border border-slate-300 bg-white px-md text-sm font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
              >
                {t("checkout.back")}
              </button>
            </div>
          </div>
        )}

        {stage.kind === "ready" && tier !== null && (
          <form
            data-testid="checkout-form"
            onSubmit={handleSubmit}
            className="mt-lg space-y-md"
          >
            {/*
              ROADS-451 PR-4: lane-specific upgrade messaging.
              - Stripe lane: client-side credit math is reliable — the
                FE-04 callout shows "{{days}} days → ${{credit}}".
              - Paddle lane: proration runs server-side on PATCH
                /subscriptions/{id} and there's no client-side estimate
                to display. Show a neutral blurb instead so the user
                still knows their remaining tourist days will roll
                forward. The credit-math <UpgradeCallout> component is
                deliberately not rendered on this path.
            */}
            {stage.billingProvider === "paddle" &&
            upgradeFromIdParam &&
            !isUpgradeDestinationTourist(tier) ? (
              <p
                data-testid="paddle-upgrade-blurb"
                className="rounded-xl border border-amber-200 bg-amber-50 px-md py-sm text-sm text-amber-900"
              >
                {t("checkout.paddleUpgradeBlurb", {
                  defaultValue:
                    "Your remaining Tourist plan days will be credited automatically at upgrade.",
                })}
              </p>
            ) : stage.upgradeFrom ? (
              <UpgradeCallout
                daysRemaining={daysRemainingFor(
                  stage.upgradeFrom.expires_at_ms,
                  stage.nowMs,
                )}
                creditUsd={creditEstimateUsd(
                  daysRemainingFor(
                    stage.upgradeFrom.expires_at_ms,
                    stage.nowMs,
                  ),
                )}
              />
            ) : null}

            {tierRequiresState(tier) && (
              <div>
                <label
                  htmlFor="checkout-state"
                  className="block text-sm font-semibold text-slate-900"
                >
                  {t("checkout.field.state")}
                </label>
                <p className="mt-xs text-xs text-slate-500">
                  {t("checkout.field.stateHelp")}
                </p>
                <input
                  id="checkout-state"
                  data-testid="checkout-state-input"
                  type="text"
                  required
                  inputMode="text"
                  autoComplete="off"
                  aria-required="true"
                  pattern="[A-Za-z]{2}"
                  maxLength={2}
                  value={stateCode}
                  onChange={(e) => setStateCode(e.target.value.toUpperCase())}
                  className="mt-xs h-11 w-full rounded-xl border border-slate-300 bg-white px-md text-base text-slate-900 focus:border-teal-600 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-teal-600"
                />
              </div>
            )}

            {tierRequiresCountry(tier) && (
              <div>
                <label
                  htmlFor="checkout-country"
                  className="block text-sm font-semibold text-slate-900"
                >
                  {t("checkout.field.country")}
                </label>
                <p className="mt-xs text-xs text-slate-500">
                  {t("checkout.field.countryHelp")}
                </p>
                <select
                  id="checkout-country"
                  data-testid="checkout-country-input"
                  required
                  aria-required="true"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="mt-xs h-11 w-full rounded-xl border border-slate-300 bg-white px-md text-base text-slate-900 focus:border-teal-600 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-teal-600"
                >
                  <option value="">{t("checkout.field.countryPlaceholder")}</option>
                  {sortedCountries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {tier === "tourist_in" && (
              <p className="rounded-xl bg-slate-50 px-md py-sm text-sm text-slate-700">
                {t("checkout.touristInBlurb")}
              </p>
            )}

            <div className="flex flex-wrap gap-sm pt-sm">
              <button
                type="submit"
                data-testid="checkout-submit"
                className="inline-flex h-11 min-w-[44px] items-center justify-center rounded-xl bg-teal-600 px-md text-sm font-semibold text-white hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
              >
                {t("checkout.submit")}
              </button>
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex h-11 min-w-[44px] items-center justify-center rounded-xl border border-slate-300 bg-white px-md text-sm font-semibold text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700"
              >
                {t("checkout.back")}
              </button>
            </div>

            <p className="pt-sm text-xs text-slate-500">
              {t("checkout.disclaimer")}
            </p>
          </form>
        )}
      </section>
    </LearnerPortalShell>
  );
}

/** Map an EntitlementTier to the i18n leaf used in the paywall block. */
function tierI18nKey(tier: EntitlementTier): string {
  switch (tier) {
    case "class_c":
      return "classC";
    case "cdl":
      return "cdl";
    case "tourist_in":
      return "tourist";
    case "tourist_out":
      return "outbound";
    case "motorcycle":
      return "classC";
  }
}

